import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import styled from 'styled-components';
import { LayoutWithModal } from 'hocs';
import { userActions } from 'actions';
import Navigation from 'components/Core/Utils/Navigation';
import FilterMenu from 'components/Filter/FilterMenu';
import Footer from 'components/Core/Utils/Footer';
import ModalOrderDisabled from 'components/Modal/ModalOrderDisabled';
import { selectCurrentPlatform, isPlatformDeactivated } from 'selectors/platform';
import { getValueOfAttribut } from 'helpers/Platform';
import { userConstants, platformConstants } from '../constants';
import Loading from '../pages/Loading/Loading';

const ContentWrapper = styled.div`
  margin-top: 61px;
  min-height: calc(
    (var(--vh, 1vh) * 100) - ${(props) => (props.hasFooter ? '121px' : '61px')}
      /* use custom vh property to prevent vertical shift on mobile browsers */
  );
`;

class Layout extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loadedPlatformId: null,
      platformSwitched: false,
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const nextSelectedId = nextProps.platform.selectedId;
    // Capture the first non-null selectedId
    if (prevState.loadedPlatformId === null && nextSelectedId !== null) {
      return { loadedPlatformId: nextSelectedId };
    }
    // Detect platform switch after initial load (runs BEFORE render)
    if (
      prevState.loadedPlatformId !== null &&
      nextSelectedId !== null &&
      Number(prevState.loadedPlatformId) !== Number(nextSelectedId)
    ) {
      return { platformSwitched: true };
    }
    return null;
  }

  componentDidMount() {
    const { user, fetchUserInformation } = this.props;
    const { information: userInformation, isLoading: userLoading } = user;
    if (!Object.keys(userInformation).length && userLoading !== true) {
      fetchUserInformation();
    }
  }

  hasFooter() {
    const { user } = this.props;

    return user.information.type === userConstants.TYPE_CUSTOMER;
  }

  isOrderDisabled() {
    const { user, isOrderDisabledOnPlatform } = this.props;
    const userType = user.information.type;

    // Only show for customers (including login-as)
    if (userType !== userConstants.TYPE_CUSTOMER) {
      return false;
    }
    // Don't show on landing page so user can change platform
    if (window.location.pathname === '/landing') {
      return false;
    }
    // Don't show if platform was switched mid-session (location.href reload is pending)
    if (this.state.platformSwitched) {
      return false;
    }
    return isOrderDisabledOnPlatform;
  }

  // A deactivated platform must not be usable: send the user back to /landing,
  // where the existing "nouvelle version de commande.com" message (or the platform
  // picker, if other platforms are still active) is shown. Applies to customers,
  // commercials and admins — /landing is opened to those roles so the redirect
  // resolves. Super admins are excluded: their flow is /configuration and LandingPage
  // bounces them there anyway (would render once and warn before redirecting).
  // Login-as is intentionally excluded upstream (isCurrentPlatformDeactivated).
  shouldRedirectToLanding() {
    const { user, isCurrentPlatformDeactivated } = this.props;

    if (user.information.type === userConstants.TYPE_SUPER_ADMIN) {
      return false;
    }
    // /landing renders outside Layout, but guard anyway to make the intent explicit.
    if (window.location.pathname === '/landing') {
      return false;
    }
    return isCurrentPlatformDeactivated;
  }

  render() {
    const { component: Component, filter, loading, hasGrid, ...props } = this.props;
    const { isOpen, ...filterProps } = filter;
    const { canDisplayWebSite } = loading;

    // Wait until platform data is loaded before deciding, otherwise the empty
    // current platform would read as "not deactivated" and we'd let the user in.
    if (canDisplayWebSite && this.shouldRedirectToLanding()) {
      return <Redirect to="/landing" />;
    }

    return (
      <main>
        {isOpen && <FilterMenu {...filterProps} />}
        {canDisplayWebSite && (
          <div className="navigation fade-in">
            <Navigation />
          </div>
        )}
        {canDisplayWebSite && (
          <ContentWrapper hasFooter={this.hasFooter()} className="content fade-in">
            <Component {...props} />
          </ContentWrapper>
        )}
        {canDisplayWebSite && <Footer match={props.match || {}} />}
        {!canDisplayWebSite && (
          <div className="fade-in">
            <Loading {...props} />
          </div>
        )}
        {canDisplayWebSite && this.isOrderDisabled() && <ModalOrderDisabled />}
      </main>
    );
  }
}

const mapStateToProps = (state) => {
  const { user, filter, platform, message, loading, authentication } = state;
  const currentPlatform = selectCurrentPlatform(state);
  const { attributs } = currentPlatform;
  const isOrderDisabledOnPlatform = !!Number(
    getValueOfAttribut(attributs || [], platformConstants.KEY_DISABLE_PROCESS_CART_TO_ORDER)
  );
  // When an admin is logged-as a customer (X-Switch-User), let them in even on a
  // deactivated platform — mirrors selectActivePlatformList, which returns all
  // platforms unfiltered in that case.
  const loggedAs = !!(authentication && authentication.loggedAs);
  const isCurrentPlatformDeactivated = !loggedAs && isPlatformDeactivated(currentPlatform);
  return {
    user,
    filter,
    platform,
    message,
    loading,
    isOrderDisabledOnPlatform,
    isCurrentPlatformDeactivated,
  };
};

const mapDispatchToProps = (dispatch) => ({
  fetchUserInformation: () => {
    const storedPlatformId = localStorage.getItem('platform');
    const platformId = storedPlatformId ? Number(storedPlatformId) : null;
    return dispatch(userActions.fetchInformation(false, platformId));
  },
});

Layout.propTypes = {
  user: PropTypes.object,
  filter: PropTypes.object,
  fetchUserInformation: PropTypes.func,
  component: PropTypes.func,
  loading: PropTypes.object,
  hasGrid: PropTypes.bool,
};

Layout.defaultProps = {
  hasGrid: false,
};

const ConnectedLayout = connect(mapStateToProps, mapDispatchToProps)(Layout);

const WithModal = (props) => <LayoutWithModal layout={ConnectedLayout} {...props} />;

export default WithModal;
